import { useEffect, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

export default function LessonDrawer({
  title,
  onClose,
  children,
  eyebrow = "Lesson library",
  closeLabel = "Close lesson details",
  className = "",
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  eyebrow?: string;
  closeLabel?: string;
  className?: string;
}) {
  const panel = useRef<HTMLElement>(null);
  const backdrop = useRef<HTMLDivElement>(null);
  const close = useRef(onClose);
  close.current = onClose;
  // A drag that starts in the panel (e.g. selecting text) and ends on the
  // backdrop still fires click there; only a press that began on it closes.
  const isPressOnBackdrop = useRef(false);
  useEffect(() => {
    const previousFocus = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const background = Array.from(document.body.children).filter(
      (el): el is HTMLElement =>
        el instanceof HTMLElement && el !== backdrop.current,
    );
    const inertState = background.map((el) => [el, el.inert] as const);
    background.forEach((el) => {
      el.inert = true;
    });
    panel.current?.focus();
    return () => {
      document.body.style.overflow = overflow;
      inertState.forEach(([el, wasInert]) => {
        el.inert = wasInert;
      });
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, []);
  return createPortal(
    <div
      ref={backdrop}
      className="et-dialog-backdrop et-product-surface"
      onMouseDown={(e) => {
        isPressOnBackdrop.current = e.target === e.currentTarget;
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && isPressOnBackdrop.current)
          close.current();
        isPressOnBackdrop.current = false;
      }}
    >
      <section
        ref={panel}
        className={`et-lesson-drawer ${className}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.stopPropagation();
            close.current();
          }
          if (e.key !== "Tab") return;
          const targets = Array.from(
            panel.current?.querySelectorAll<HTMLElement>(
              'button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex="0"]',
            ) ?? [],
          ).filter((el) => el.getClientRects().length > 0);
          const first = targets[0],
            last = targets[targets.length - 1];
          if (!first) {
            e.preventDefault();
            return;
          }
          if (
            e.shiftKey &&
            (document.activeElement === first ||
              document.activeElement === panel.current)
          ) {
            e.preventDefault();
            last.focus();
          }
          if (
            !e.shiftKey &&
            (document.activeElement === last ||
              document.activeElement === panel.current)
          ) {
            e.preventDefault();
            first.focus();
          }
        }}
      >
        <div className="et-drawer-top">
          <span>{eyebrow}</span>
          <button
            type="button"
            className="et-icon-button"
            onClick={onClose}
            aria-label={closeLabel}
          >
            <X size={20} />
          </button>
        </div>
        {children}
      </section>
    </div>,
    document.body,
  );
}
